type JsonObject = Record<string, unknown>;
const DEFAULT_DATASET_LICENSE_URL = 'https://rentsmart.ie/terms-of-service';
const DEFAULT_DATASET_CREATOR = {
  '@type': 'Organization',
  name: 'RentSmart',
  url: 'https://rentsmart.ie',
} as const;

const isObject = (value: unknown): value is JsonObject =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

/**
 * Adds fallback metadata to any Dataset nodes missing key fields.
 * This helps keep JSON-LD valid when older stored schema payloads are incomplete.
 */
export const ensureDatasetDescriptions = (
  schema: unknown,
  fallbackDescription: string,
  fallbackLicense: string = DEFAULT_DATASET_LICENSE_URL
): unknown => {
  if (Array.isArray(schema)) {
    return schema.map((item) =>
      ensureDatasetDescriptions(item, fallbackDescription, fallbackLicense)
    );
  }

  if (!isObject(schema)) {
    return schema;
  }

  const normalizedDescription = String(fallbackDescription || '').trim();
  const normalizedLicense = String(fallbackLicense || '').trim();
  const next: JsonObject = {};

  Object.entries(schema).forEach(([key, value]) => {
    next[key] = ensureDatasetDescriptions(value, normalizedDescription, normalizedLicense);
  });

  const typeValue = String(next['@type'] || '').toLowerCase();
  const currentDescription = String(next.description || '').trim();
  const currentLicense = String(next.license || '').trim();
  const currentCreator = next.creator;
  if (typeValue === 'dataset' && !currentDescription && normalizedDescription) {
    next.description = normalizedDescription;
  }
  if (typeValue === 'dataset' && !currentLicense && normalizedLicense) {
    next.license = normalizedLicense;
  }
  if (
    typeValue === 'dataset' &&
    (!currentCreator || (typeof currentCreator !== 'object' && typeof currentCreator !== 'string'))
  ) {
    next.creator = DEFAULT_DATASET_CREATOR;
  }

  return next;
};
